import { Supporter, SupporterList } from '@/types';
import { adjustPageNo } from '@/util';
import ButtonRow from '@components/ButtonRow';
import DetailedContactsTable from '@components/DetailedContactsTable';
import TwoSidedSwitch from '@components/TwoSidedSwitch';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Menu, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';
import ReleaseContactsDialog from '@/components/ReleaseContactsDialog';
import { CompleteListDialog } from './components/CompleteListDialog';
import ReleaseSelectedContactsDialog from './components/ReleaseSelectedContactsDialog';
import { PanelContent } from './types';

interface DetailTabProps {
  disableToggle: boolean;
  pageNo: number;
  setPageNo: Dispatch<SetStateAction<number>>;
  setFilterOptions: Dispatch<SetStateAction<GridFilterModel>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
  setCallingViewContactIndex: Dispatch<SetStateAction<number>>;
  changeTab: (destinationTab: string) => void;
  onSortModelChange: (model: GridSortModel) => void;
  sortModel: GridSortModel;
  rightPanelOpen: boolean;
  handleOpenRightPanel: (content: PanelContent) => void;
  listObjIsACoordinator: boolean;
  list: SupporterList | undefined;
  userIsCaller: boolean;
  isLoading: boolean;
  setEnterCallingByTableClick: Dispatch<SetStateAction<boolean>>;
  selectedContacts: Supporter[];
  setSelectedContacts: Dispatch<SetStateAction<Supporter[]>>;
}

export const AllContactsSwitch = ({
  isRightSelected,
  onSwitchValueChanged,
  isDisabled,
}: {
  isRightSelected: boolean;
  onSwitchValueChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  isDisabled: boolean;
}) => (
  <TwoSidedSwitch
    sx={{ paddingLeft: '17px', height: '42px' }}
    leftSideText={'My Contacts'}
    rightSideText={'All Contacts'}
    twoSidedSwitchDisabledColour={'rgba(0, 0, 0, 0.54)'}
    isRightSelected={isRightSelected}
    onSwitchValueChanged={onSwitchValueChanged}
    disabled={isDisabled}
  />
);

export default function DetailTab({
  disableToggle,
  pageNo,
  setPageNo,
  setFilterOptions,
  pageSize,
  setPageSize,
  setCallingViewContactIndex,
  changeTab,
  onSortModelChange,
  sortModel,
  rightPanelOpen,
  handleOpenRightPanel,
  listObjIsACoordinator,
  userIsCaller,
  list,
  isLoading,
  setEnterCallingByTableClick,
  selectedContacts,
  setSelectedContacts,
}: DetailTabProps) {
  const [openCompletionDialog, setOpenCompletionDialog] = useState(false);
  const [isReleaseContactsDialogOpen, setIsReleaseContactsDialogOpen] = useState(false);
  const [isReleaseSelectedContactsDialogOpen, setIsReleaseSelectedContactsDialogOpen] =
    useState(false);

  const [releaseContactsState, setReleaseContactsState] = useState('');
  const [toggleValue, setToggleValue] = useState<'me' | 'all'>('me');
  const [disableMeToggle, setDisableMeToggle] = useState(false);

  // Keep previous rowCount during loading to prevent MUI DataGrid from resetting page to 0
  const [rowCountState, setRowCountState] = useState(list?.count ?? 0);
  /* eslint-disable react-you-might-not-need-an-effect/no-derived-state -- keep previous rowCount while the list is loading */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- keep previous rowCount while the list is loading
    setRowCountState((prev) => (list?.count !== undefined ? list.count : prev));
  }, [list?.count]);

  const offset = pageNo * pageSize;
  const handleCompleteListDialogOpen = () => {
    if (!userIsCaller && !listObjIsACoordinator)
      console.warn(
        'User should not be able to complete list. User has no associated role with this list'
      );

    setOpenCompletionDialog(true);
  };

  const handleCompleteListDialogClose = () => {
    setOpenCompletionDialog(false);
  };

  const contactRecords = list?.contactRecords ?? [];

  const areSelectedContactsValidForRelease = !selectedContacts.some(
    (contact) =>
      ['No answer', 'Answered', 'Meaningful interaction'].includes(contact.callingStatus) ||
      contact.assignedToSub === ''
  );

  const someValidForReleaseSkipped = contactRecords.some((x) => x.callingStatus === 'Skipped');
  const someValidForReleaseUncalled = contactRecords.some(
    (x) => x.callingStatus === 'Uncalled' && x.assignedToSub !== ''
  );

  const enableReleaseContactButton =
    list &&
    (((!disableToggle || listObjIsACoordinator) &&
      (someValidForReleaseSkipped || someValidForReleaseUncalled)) ||
      (disableToggle &&
        (list.callStatisticsForUser.Skipped || list.callStatisticsForUser.Uncalled)));

  const enableUncalledMenu =
    list &&
    (((!disableToggle || listObjIsACoordinator) && someValidForReleaseUncalled) ||
      (disableToggle && list.callStatisticsForUser.Uncalled));

  const enableSkippedMenu =
    list &&
    (((!disableToggle || listObjIsACoordinator) && someValidForReleaseSkipped) ||
      (disableToggle && list.callStatisticsForUser.Skipped));

  const handleReleaseContactsDialogClose = () => {
    setIsReleaseContactsDialogOpen(false);

    // LIST-377 AC5: No applicable contacts for “me” selection
    // Given I am a coordinator
    // And I have opened a list with the “My/All” contacts toggle enabled
    // And for the contacts I am assigned I don’t have any contacts with the status “Uncalled” and/or “skipped”
    // And there are contacts not assigned to me that with the status “Uncalled” and/or “skipped”
    // When I select an option from the flyaway option for a status that isn’t applicable to my contacts
    // Then the “Release remaining contacts” modal opens
    // And the “All” toggle option is selected
    // And the “Me” toggle is disabled

    // LIST-377 AC6: Clear selection of “all” for coordinators
    // Given I am a coordinator
    // And I have opened a list with the “My/All” contacts toggle enabled
    // And I have selected “All”
    // When I click the “Cancel” button
    // Then the modal closes
    // And when I navigate back to the “Release remaining contacts” modal
    // Then the modal has returned to the default state (“My” selected)

    if (disableMeToggle) {
      setToggleValue('all');
    } else {
      setToggleValue('me');
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem"
      }}>
      <DetailedContactsTable
        paginationMode="server"
        filterMode="server"
        onFilterModelChange={(model) => {
          setPageNo(0);
          setFilterOptions(model);
        }}
        isLoading={isLoading}
        rowCount={rowCountState}
        data={isLoading ? [] : contactRecords}
        paginationModel={{ page: pageNo, pageSize }}
        onPaginationModelChange={(model) => {
          setPageSize(model.pageSize);
          setPageNo(
            model.pageSize !== pageSize
              ? adjustPageNo(pageSize, pageNo, model.pageSize)
              : model.page
          );
        }}
        onClickHandler={(paramId) => {
          setCallingViewContactIndex(
            (contactRecords.findIndex((supporter) => supporter.id === paramId) ?? 0) + offset
          );
          setEnterCallingByTableClick(true);
          changeTab('calling');
        }}
        onSortModelChange={onSortModelChange}
        sortModel={sortModel}
        supporterSelectionModel={selectedContacts}
        setSupporterSelectionModel={(value) => setSelectedContacts(value)}
      />
      <ButtonRow noMarginX sx={{ margin: 0 }}>
        {listObjIsACoordinator && (
          <Button
            variant="outlined"
            disabled={list?.count === 0 || isLoading || rightPanelOpen}
            onClick={() => handleOpenRightPanel('otherActions')}
          >
            Other Actions
          </Button>
        )}

        {selectedContacts.length > 0 ? (
          <Tooltip
            title={
              areSelectedContactsValidForRelease
                ? ''
                : 'One or more selected contacts are either unassigned or have already been called'
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                disabled={!areSelectedContactsValidForRelease}
                onClick={() => {
                  setIsReleaseSelectedContactsDialogOpen(true);
                }}
              >
                Release contacts
              </Button>
            </span>
          </Tooltip>
        ) : (
          <PopupState variant="popover" popupId="demo-popup-menu">
            {(popupState) => {
              const triggerReleaseContactsDialog = (dropdownText: string) => {
                if (!disableToggle || listObjIsACoordinator) {
                  switch (dropdownText) {
                    case 'uncalled':
                      if (list?.callStatisticsForUser.Uncalled === 0) {
                        setToggleValue('all');
                        setDisableMeToggle(true);
                      } else {
                        setToggleValue('me');
                        setDisableMeToggle(false);
                      }
                      break;
                    case 'skipped':
                      if (list?.callStatisticsForUser.Skipped === 0) {
                        setToggleValue('all');
                        setDisableMeToggle(true);
                      } else {
                        setToggleValue('me');
                        setDisableMeToggle(false);
                      }
                      break;
                    default: // uncalled and skipped
                      if (
                        list?.callStatisticsForUser.Skipped === 0 &&
                        list?.callStatisticsForUser.Uncalled === 0
                      ) {
                        setToggleValue('all');
                        setDisableMeToggle(true);
                      } else {
                        setToggleValue('me');
                        setDisableMeToggle(false);
                      }
                      break;
                  }
                }

                setReleaseContactsState(dropdownText);
                setIsReleaseContactsDialogOpen(true);
                popupState.close();
              };

              return (
                <>
                  <Button
                    variant="outlined"
                    endIcon={<KeyboardArrowDownIcon />}
                    disabled={!enableReleaseContactButton}
                    {...bindTrigger(popupState)}
                  >
                    Release contacts
                  </Button>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      sx={{ display: 'flex', justifyContent: 'flex-end' }}
                      onClick={() => {
                        triggerReleaseContactsDialog('uncalled');
                      }}
                      disabled={!enableUncalledMenu}
                    >
                      Uncalled
                    </MenuItem>
                    <MenuItem
                      sx={{ display: 'flex', justifyContent: 'flex-end' }}
                      onClick={() => {
                        triggerReleaseContactsDialog('skipped');
                      }}
                      disabled={!enableSkippedMenu}
                    >
                      Skipped
                    </MenuItem>
                    <MenuItem
                      sx={{ display: 'flex', justifyContent: 'flex-end' }}
                      onClick={() => {
                        triggerReleaseContactsDialog('uncalled and skipped');
                      }}
                      disabled={!enableUncalledMenu || !enableSkippedMenu}
                    >
                      Uncalled and skipped
                    </MenuItem>
                  </Menu>
                </>
              );
            }}
          </PopupState>
        )}

        <Button
          variant="outlined"
          disabled={
            rightPanelOpen ||
            list?.callStatistics?.Uncalled === 0 ||
            (userIsCaller && !listObjIsACoordinator && list?.callStatisticsForUser.Uncalled === 0)
          }
          onClick={handleCompleteListDialogOpen}
        >
          Complete list
        </Button>
      </ButtonRow>

      <CompleteListDialog
        userIsCoordinator={listObjIsACoordinator}
        userIsCaller={userIsCaller}
        list={list}
        isOpen={openCompletionDialog}
        onClose={handleCompleteListDialogClose}
        key={openCompletionDialog ? 'completeDialogOpen' : 'completeDialogClosed'}
      />
      <ReleaseSelectedContactsDialog
        isOpen={isReleaseSelectedContactsDialogOpen}
        onClose={() => setIsReleaseSelectedContactsDialogOpen(false)}
        selectedContacts={selectedContacts}
        list={list}
        setSelectedContacts={setSelectedContacts}
      />
      <ReleaseContactsDialog
        isOpen={isReleaseContactsDialogOpen}
        onClose={handleReleaseContactsDialogClose}
        isACoordinator={
          !disableToggle ||
          (list?.callStatisticsForUser.Skipped === 0 && list?.callStatisticsForUser.Uncalled === 0)
        }
        releaseContactsState={releaseContactsState}
        listId={list?.id || 0}
        listName={list?.name || ''}
        disableMeToggle={disableMeToggle}
        toggleValue={toggleValue}
        setToggleValue={setToggleValue}
      />
    </Box>
  );
}
